import { motion } from "framer-motion";
import { Mail, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const SECTIONS = [
  {
    title: "Overview",
    body: [
      "TriviaPro (\"we\", \"our\", \"the app\") is a fair-play family trivia game for kids and adults. This Privacy Policy explains what information the app and this website collect, how we use it, and the choices you have. We designed TriviaPro to collect as little data as possible – you can play a full game without creating an account.",
    ],
  },
  {
    title: "Information We Collect",
    body: [
      "We keep data collection to a minimum:",
    ],
    list: [
      "Game data you create: player nicknames, avatars, scores, league standings, and game progress. Nicknames do not need to be real names.",
      "Purchase information: if you buy a subscription or question pack, the transaction is processed by Apple and our purchase partner – we never see or store your payment card details.",
      "Device and app data: basic technical information such as device model, operating system version, app version, and crash diagnostics, used to keep the app running well.",
    ],
  },
  {
    title: "How We Use Your Information",
    list: [
      "To run the game: saving scores, leagues, and unlocked question packs.",
      "To process and restore purchases across your devices.",
      "To fix bugs, prevent crashes, and improve question content.",
      "To respond when you contact us for support.",
    ],
    body: [
      "We do not sell your personal information, and we do not use your data for advertising. There are no ads in TriviaPro.",
    ],
  },
  {
    title: "Third-Party Services",
    body: [
      "TriviaPro relies on a small number of trusted providers:",
    ],
    list: [
      "RevenueCat – manages subscriptions and in-app purchases. RevenueCat receives an anonymous app user ID and purchase receipts from Apple to verify and restore entitlements. It does not receive your name, email, or payment card details from us.",
      "MongoDB – stores game data such as nicknames, scores, and progress securely in the cloud so your family league survives a lost phone. Data is encrypted in transit and access is restricted.",
      "Apple App Store – handles all payment processing under your Apple ID and Apple's own privacy policy.",
      "Google Analytics (this website only) – counts visitors and page views on trivialabs.org using aggregated, de-identified data. It is not used inside the app.",
    ],
  },
  {
    title: "Children's Privacy (COPPA)",
    body: [
      "TriviaPro is built for families, including children aged 6 and up, and we comply with the U.S. Children's Online Privacy Protection Act (COPPA).",
    ],
    list: [
      "The app does not require children to create an account or provide a name, email address, phone number, or any other personal information.",
      "Player profiles use nicknames chosen at the device, and games are played locally, pass-and-play style.",
      "We do not knowingly collect personal information from children under 13. If you believe a child has provided us personal information, contact us and we will delete it promptly.",
      "There are no third-party advertisements and no behavioural tracking of children.",
    ],
  },
  {
    title: "Cookies & Similar Technologies",
    body: [
      "The TriviaPro app does not use cookies. This website uses Google Analytics cookies (such as _ga) to count visitors and see which pages are read. We do not use advertising or cross-site tracking cookies.",
    ],
  },
  {
    title: "Analytics",
    body: [
      "This website uses Google Analytics (GA4) to understand visitor numbers and which pages are popular. Analytics data is aggregated and de-identified, is never linked to a child, and is never sold or used for advertising. The TriviaPro app itself includes no behavioural tracking.",
    ],
  },
  {
    title: "Data Retention & Deletion",
    body: [
      "We keep game data only for as long as your family league is active. You can delete local player profiles at any time inside the app.",
      "To request deletion of any cloud-stored game data, or any personal information associated with a purchase, email us at the address below. We will confirm and complete verified deletion requests within 30 days.",
    ],
  },
  {
    title: "International Users",
    body: [
      "TriviaPro is operated from Australia, and game data may be processed in other countries where our service providers (such as MongoDB's cloud hosting) operate. Where required, we rely on appropriate safeguards for international data transfers. If you are in the EU/EEA or UK, you have the right to access, correct, export, or erase your personal data – just email us.",
    ],
  },
  {
    title: "Data Security",
    body: [
      "We use industry-standard measures including encryption in transit (TLS) and restricted, credential-based access to our databases. No method of transmission or storage is 100% secure, but protecting your family's data is a rule we never break.",
    ],
  },
  {
    title: "Changes to This Policy",
    body: [
      "If we update this Privacy Policy, we will post the new version on this page and update the \"last updated\" date above. For significant changes, we will also notify you inside the app.",
    ],
  },
];

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="mx-auto max-w-3xl px-6 pb-28 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="pt-20 md:pt-28"
      >
        <p className="inline-flex items-center gap-2 rounded-full border-2 border-[#1A1D1E] bg-[#FFC837] px-5 py-2 text-xs font-extrabold uppercase tracking-[0.2em] shadow-[0_4px_0_0_#1A1D1E]">
          <ShieldCheck className="h-4 w-4" />
          Your privacy, in plain words
        </p>
        <h1 className="mt-8 font-display text-5xl font-bold tracking-tight md:text-6xl">
          Privacy Policy
        </h1>
        <p className="mt-4 text-sm font-bold uppercase tracking-widest text-[#6B7280]">
          TriviaPro · Last updated: July 2026
        </p>
      </motion.div>

      <div className="mt-16 flex flex-col gap-10">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={0.03}>
            <section
              data-testid={`privacy-section-${i + 1}`}
              className="rounded-[20px] border-2 border-[#1A1D1E] bg-white p-8 shadow-[0_6px_0_0_#1A1D1E]"
            >
              <div className="flex items-baseline gap-4">
                <span className="font-display text-lg font-bold text-[#1FBCA2]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="font-display text-2xl font-semibold tracking-tight">
                  {s.title}
                </h2>
              </div>
              {s.body?.map((p) => (
                <p key={p.slice(0, 24)} className="mt-4 leading-relaxed text-[#6B7280]">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-4 flex flex-col gap-3">
                  {s.list.map((item) => (
                    <li key={item.slice(0, 24)} className="flex gap-3 leading-relaxed text-[#6B7280]">
                      <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-[#FFC837]" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </Reveal>
        ))}

        <Reveal>
          <section
            data-testid="privacy-contact-section"
            className="rounded-[20px] border-2 border-[#1A1D1E] bg-[#1FBCA2] p-8 shadow-[0_6px_0_0_#1A1D1E]"
          >
            <div className="flex items-baseline gap-4">
              <span className="font-display text-lg font-bold text-[#1A1D1E]/60">12</span>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#1A1D1E]">
                Contact Us
              </h2>
            </div>
            <p className="mt-4 leading-relaxed text-[#1A1D1E]/75">
              Questions about this policy, your data, or a deletion request?
              Email us anytime – a real human reads every message.
            </p>
            <a
              href="mailto:info@trivialabs.org"
              data-testid="privacy-contact-email"
              className="mt-6 inline-flex items-center gap-3 rounded-full border-2 border-[#1A1D1E] bg-white px-7 py-3.5 font-display text-base font-semibold text-[#1A1D1E] shadow-[0_5px_0_0_#1A1D1E] transition-[transform,box-shadow] duration-150 hover:translate-y-[2px] hover:shadow-[0_3px_0_0_#1A1D1E] active:translate-y-[5px] active:shadow-none focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FFC837]/60"
            >
              <Mail className="h-5 w-5" />
              info@trivialabs.org
            </a>
            <p className="mt-5 text-sm font-bold text-[#1A1D1E]/70">
              Need help with the app instead? Email{" "}
              <a
                href="mailto:support@trivialabs.org"
                data-testid="privacy-support-email"
                className="underline decoration-[#FFC837] decoration-2 underline-offset-4 transition-colors duration-200 hover:text-[#1A1D1E]"
              >
                support@trivialabs.org
              </a>
            </p>
          </section>
        </Reveal>
      </div>
    </main>
  );
}
